/**
 * AWS GeoMaps のスタイル記述子に含まれる MapLibre style-spec 違反を読み込み時に補正する。
 *
 * 新世代 GeoMaps（`v2/styles/<Style>/descriptor`）の公式スタイル定義には、MapLibre の
 * 式の型要件を満たさない箇所があり、Chrome DevTools に大量の警告が出て開発時のコンソールが
 * 実質使えなくなる。描画自体は MapLibre 側のフォールバックで破綻しないが、ノイズを消すため
 * 読み込み時にスタイル JSON を補正する（`mapStyle.ts` のカスタムプロトコル経由）。
 *
 * 補正する2種類（いずれも実機の記述子で確認済み。AWS 側が修正した場合も本補正は無害）:
 *
 * 1. number を要求する式の被演算子が `["get", key]` で、その属性を持たないフィーチャでは
 *    null になる。`["coalesce", ["get", key], 0]` で包んで解消する。
 *      例) `[">", ["get","z_level"], 0]`（filter）
 *          `["*", -1, ["get","population"]]`（symbol-sort-key）
 *      → `Expected value to be of type number, but found null instead. Falling back to ...`
 *
 *    - 数値比較（`>` `>=` `<` `<=`）は文字列同士の比較も許されるため、**相手に数値リテラルが
 *      いる場合だけ**包む（文字列比較を壊さないため）。3スタイルの該当 225 箇所すべてが
 *      数値リテラルとの比較であることを実測確認済み。null→false と 0→false は同結果なので
 *      filter の意味論は不変。
 *    - 算術演算（`+` `-` `*` `/` `%` `^`）は spec 上そもそも number 専用なので無条件に包む
 *      （実際に現れるのは `-` と `*` のみ）。
 *
 * 2. `symbol-sort-key` が `["get", key]` で、文字列を返す属性を指している。
 *      例) `["get","pds_category"]`（POI 12 レイヤー）
 *      → `Expected value to be of type number, but found string instead.`
 *    `["to-number", ["get", key], 0]` で包む。数値文字列なら AWS の意図した優先度が復活し、
 *    数値化できなければ 0（= 補正前と同じ「効かない」状態）に落ちる。どちらでも警告は出ない。
 *
 * 検証: 3スタイルの記述子を `@maplibre/maplibre-gl-style-spec` で実機と同じ手順で評価し
 * （filter を評価し、通ったレイヤーだけ layout/paint を評価する）、補正前に出ていた実行時型
 * エラーが補正後は3スタイルすべてで 0 件になることを確認した。
 *
 * 参照:
 *   https://maplibre.org/maplibre-style-spec/expressions/#%3E
 *   https://maplibre.org/maplibre-style-spec/layers/#symbol-sort-key
 */

/**
 * 比較演算子。number だけでなく string 同士の比較も許されるため、数値リテラルと
 * 比較している場合に限って補完する。
 */
const COMPARE_OPS: ReadonlySet<string> = new Set(['>', '>=', '<', '<=']);

/** 算術演算子。被演算子は spec 上 number 専用なので無条件に補完できる。 */
const ARITHMETIC_OPS: ReadonlySet<string> = new Set([
  '+',
  '-',
  '*',
  '/',
  '%',
  '^',
]);

/** 式を含みうるレイヤーのプロパティ。 */
const EXPRESSION_HOLDERS = ['filter', 'layout', 'paint'] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** `["get", "key"]` 形式か（第2引数が静的なキー名のものだけを対象にする）。 */
function isGetExpression(value: unknown): value is [string, string] {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    value[0] === 'get' &&
    typeof value[1] === 'string'
  );
}

/**
 * number を要求する式の直下にある `["get", key]` を `["coalesce", ["get", key], 0]` に置き換える。
 * `["literal", ...]` の中身は式ではなく生データなので走査しない。
 */
function coalesceNumericGets(node: unknown): unknown {
  if (Array.isArray(node)) {
    if (node[0] === 'literal') {
      return node;
    }
    const mapped = node.map(coalesceNumericGets);
    const [op, ...args] = mapped;
    const needsNumber =
      typeof op === 'string' &&
      (ARITHMETIC_OPS.has(op) ||
        (COMPARE_OPS.has(op) && args.some((arg) => typeof arg === 'number')));
    if (needsNumber) {
      return [
        op,
        ...args.map((arg) =>
          isGetExpression(arg) ? ['coalesce', arg, 0] : arg,
        ),
      ];
    }
    return mapped;
  }
  if (isRecord(node)) {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(node)) {
      out[key] = coalesceNumericGets(value);
    }
    return out;
  }
  return node;
}

function patchLayer(layer: unknown): unknown {
  if (!isRecord(layer)) {
    return layer;
  }
  const next: Record<string, unknown> = { ...layer };
  for (const key of EXPRESSION_HOLDERS) {
    if (next[key] !== undefined) {
      next[key] = coalesceNumericGets(next[key]);
    }
  }
  const layout = next.layout;
  if (isRecord(layout) && isGetExpression(layout['symbol-sort-key'])) {
    next.layout = {
      ...layout,
      'symbol-sort-key': ['to-number', layout['symbol-sort-key'], 0],
    };
  }
  return next;
}

/**
 * スタイル記述子を補正して返す（入力は変更しない）。
 * 想定外の形（layers が無い等）ならそのまま返す＝スタイル側の仕様変更で壊れない。
 */
export function patchGeoMapsStyle(descriptor: object): object {
  const style = descriptor as Record<string, unknown>;
  if (!Array.isArray(style.layers)) {
    return descriptor;
  }
  return { ...style, layers: style.layers.map(patchLayer) };
}
